import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../features/auth';
import { useBoard } from '../../../entities/board';
import { useLeads } from '../../../entities/lead';
import { HistoryEntry, ActionHistoryEntry, Lead, SmsLog } from '../../../entities/lead';
import type { LeadsFilters } from '../../../entities/lead/model/api';
import type { Group } from '../../../entities/group';
import type { Tag } from '../../../entities/tag';
import { Topbar } from '../../../widgets/topbar';
import { MainSidebar, useSidebar } from '../../../widgets/main-sidebar';
import { KanbanBoard, LeadsTable } from '../../../widgets/kanban-board';
import { CardModal } from '../../../features/card-modal';
import { useCardModalStore } from '../../../features/card-modal';
import { CreateLeadModal } from '../../../features/create-lead';
import { FilterDropdown } from '../../../features/kanban-filter';
import { useFilterStore, getStoredLeadsFilters, toLeadsFilters } from '../../../features/kanban-filter';
import { Notify, useNotify } from '../../../shared/ui/Notify/Notify';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { pb } from '../../../shared/api/pocketbase';

const KanbanPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { stages, isLoading: isBoardLoading, fetchStages } = useBoard();
  const { fetchLeadsTable, createLead, updateLead, bulkUpdateLeads, deleteLead, getLeadHistory, getLeadActions, addHistoryEntry } = useLeads();
  const cardModal = useCardModalStore();
  const filterStore = useFilterStore();
  const { msg, notify, clear } = useNotify();
  const queryClient = useQueryClient();
  const activeLeadIdRef = useRef<string | null>(null);
  const { toggle: toggleSidebar } = useSidebar();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tableLeads, setTableLeads] = useState<Lead[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const lastFiltersRef = useRef<LeadsFilters>(getStoredLeadsFilters());
  const canUseFilters = currentUser?.role === 'MANAGER' || currentUser?.role === 'ADMIN';
  const [allTags, setAllTags] = useState<Tag[]>([]);

  useEffect(() => {
    fetchStages().catch(console.error);
    pb.collection('tags').getFullList<Tag>({ sort: 'name', expand: 'groupId', fields: 'id,name,colors,groupId,expand.groupId.id,expand.groupId.name' }).then(setAllTags).catch(() => {});
  }, [fetchStages]);

  // Дебаунс поискового запроса для канбана (колонки грузятся лениво с этим фильтром)
  useEffect(() => {
    const query = searchQuery.trim();
    const timer = window.setTimeout(() => setDebouncedSearch(query), query ? 400 : 0);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  // Активные фильтры канбана = фильтры дропдауна + поиск; уходят в постраничную загрузку колонок
  const boardFilters = useMemo<LeadsFilters | undefined>(() => {
    const f: LeadsFilters = toLeadsFilters(filterStore.filters);
    if (debouncedSearch) f.searchTerm = debouncedSearch;
    return Object.keys(f).length ? f : undefined;
  }, [filterStore.filters, debouncedSearch]);

  const isFiltered = Boolean(boardFilters);

  // --- Lead history & actions (2s stale) ---
  const activeLeadId = cardModal.isOpen ? cardModal.selectedLeadId : null;

  const { data: modalHistory = [] } = useQuery({
    queryKey: ['lead', activeLeadId, 'history'],
    queryFn: () => getLeadHistory(activeLeadId!),
    enabled: Boolean(activeLeadId),
    staleTime: 2_000,
  });

  const { data: modalActions = [] } = useQuery({
    queryKey: ['lead', activeLeadId, 'actions'],
    queryFn: () => getLeadActions(activeLeadId!),
    enabled: Boolean(activeLeadId),
    staleTime: 2_000,
  });

  const { data: smsLogs = [] } = useQuery<SmsLog[]>({
    queryKey: ['lead', activeLeadId, 'sms'],
    queryFn: () => pb.collection('sms_log').getFullList<SmsLog>({
      filter: `taskId = "${activeLeadId}"`,
      sort: '-created',
      fields: 'id,taskId,ruleId,phone,text,status,created',
    }),
    enabled: Boolean(activeLeadId) && currentUser?.role === 'ADMIN',
    staleTime: 10_000,
  });

  // --- Realtime subscriptions for open modal ---
  useEffect(() => {
    if (!cardModal.isOpen || !cardModal.selectedLeadId) return;
    const leadId = cardModal.selectedLeadId;
    activeLeadIdRef.current = leadId;

    pb.collection('comments').subscribe<HistoryEntry>('*', async (e) => {
      if (e.record.taskId !== leadId) return;
      if (e.action === 'delete') {
        queryClient.setQueryData<HistoryEntry[]>(['lead', leadId, 'history'], prev =>
          (prev ?? []).filter(h => h.id !== e.record.id)
        );
        return;
      }
      try {
        const hydrated = await pb.collection('comments').getOne<HistoryEntry>(e.record.id, {
          expand: 'userId',
          fields: 'id,taskId,text,created,expand.userId.id,expand.userId.name',
        });
        if (activeLeadIdRef.current !== leadId) return;
        queryClient.setQueryData<HistoryEntry[]>(['lead', leadId, 'history'], prev => {
          const list = prev ?? [];
          if (e.action === 'create') {
            return list.some(h => h.id === hydrated.id) ? list : [hydrated, ...list];
          }
          return list.map(h => h.id === hydrated.id ? hydrated : h);
        });
      } catch {}
    });

    pb.collection('history').subscribe<ActionHistoryEntry>('*', async (e) => {
      if (e.record.taskId !== leadId) return;
      if (e.action === 'delete') {
        queryClient.setQueryData<ActionHistoryEntry[]>(['lead', leadId, 'actions'], prev =>
          (prev ?? []).filter(a => a.id !== e.record.id)
        );
        return;
      }
      try {
        const hydrated = await pb.collection('history').getOne<ActionHistoryEntry>(e.record.id, {
          expand: 'userId',
          fields: 'id,taskId,action,created,expand.userId.id,expand.userId.name',
        });
        if (activeLeadIdRef.current !== leadId) return;
        queryClient.setQueryData<ActionHistoryEntry[]>(['lead', leadId, 'actions'], prev => {
          const list = prev ?? [];
          if (e.action === 'create') {
            return list.some(a => a.id === hydrated.id) ? list : [hydrated, ...list];
          }
          return list.map(a => a.id === hydrated.id ? hydrated : a);
        });
      } catch {}
    });

    return () => {
      activeLeadIdRef.current = null;
      pb.collection('comments').unsubscribe('*');
      pb.collection('history').unsubscribe('*');
    };
  }, [cardModal.isOpen, cardModal.selectedLeadId, queryClient]);

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [cardNotFound, setCardNotFound] = useState(false);
  const [cardNotFoundDismissed, setCardNotFoundDismissed] = useState(false);

  useEffect(() => {
    if (!cardModal.selectedLeadId) {
      setSelectedLead(null);
      setCardNotFound(false);
      setCardNotFoundDismissed(false);
      return;
    }
    setCardNotFound(false);
    setCardNotFoundDismissed(false);
    pb.collection('tasks').getOne<Lead>(cardModal.selectedLeadId, {
      expand: 'userId,boardId',
      fields: '*,expand.userId.id,expand.userId.name,expand.userId.email,expand.boardId.id,expand.boardId.name,expand.boardId.colors',
    })
      .then(setSelectedLead)
      .catch((err: { status?: number }) => {
        if (err?.status === 404) {
          setCardNotFound(true);
        } else {
          console.error(err);
        }
      });
  }, [cardModal.selectedLeadId]);


  const { data: adminGroups = [] } = useQuery<Group[]>({
    queryKey: ['groups', 'kanban-filter'],
    queryFn: () => pb.collection('groups').getFullList<Group>({ sort: 'name', fields: 'id,name,usersIds' }),
    enabled: currentUser?.role === 'ADMIN',
    staleTime: 60_000,
  });


  const visibleStages = useMemo(() => {
    if (!filterStore.filters.boardIds.length) return stages;
    return stages.filter(s => filterStore.filters.boardIds.includes(s.id));
  }, [stages, filterStore.filters.boardIds]);


  const handleSave = async (data: Partial<Lead>) => {
    if (!cardModal.selectedLeadId || !data) return;
    try {
      await updateLead(cardModal.selectedLeadId, data);
      cardModal.closeModal();
      notify('Изменения сохранены');
    } catch (error) {
      notify(getPbErrorMessage(error, 'Ошибка при сохранении'));
    }
  };

  const handleAddComment = async (text: string) => {
    if (!cardModal.selectedLeadId) return;
    try {
      await addHistoryEntry(cardModal.selectedLeadId, text);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'history'] }),
        queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'actions'] }),
      ]);
      notify('Комментарий добавлен');
    } catch {
      notify('Ошибка при добавлении комментария');
    }
  };

  const handleReloadCardActivity = async () => {
    if (!cardModal.selectedLeadId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'history'] }),
      queryClient.invalidateQueries({ queryKey: ['lead', cardModal.selectedLeadId, 'actions'] }),
    ]);
  };

  const handleApplyFilters = async (filters: Parameters<typeof filterStore.applyFilters>[0]) => {
    let resolvedFilters = filters;
    if (filters.groupIds.length) {
      const groupUserIds = adminGroups
        .filter(g => filters.groupIds.includes(g.id))
        .flatMap(g => g.usersIds ?? []);
      resolvedFilters = {
        ...filters,
        userIds: [...new Set([...filters.userIds, ...groupUserIds])],
      };
    }
    const leadsFilters = filterStore.applyFilters(resolvedFilters);
    lastFiltersRef.current = leadsFilters;
    // канбан: колонки сами перезагрузятся при смене boardFilters
    if (viewMode === 'table') {
      await loadTablePage(1, true);
    }
    notify('Фильтр применён');
  };

  const handleResetFilters = async () => {
    filterStore.resetFilters();
    lastFiltersRef.current = {};
    if (viewMode === 'table') {
      await loadTablePage(1, true);
    }
    notify('Фильтры сброшены');
  };

  const handleMoveLead = async (leadId: string, stageId: string) => {
    try {
      await updateLead(leadId, { boardId: stageId });
      notify('Карточка перенесена');
    } catch (error) {
      notify(getPbErrorMessage(error, 'Ошибка при переносе карточки'));
    }
  };

  const handleBulkUpdate = async (leadIds: string[], patch: Partial<Lead>, label: string) => {
    try {
      await bulkUpdateLeads(leadIds, patch);
      await queryClient.invalidateQueries({ queryKey: ['column'] });
      notify(label);
    } catch (error) {
      notify(getPbErrorMessage(error, 'Ошибка при массовом обновлении'));
    }
  };

  const handleDeleteLead = async (lead: Lead) => {
    try {
      await deleteLead(lead.id);
      cardModal.closeModal();
      notify('Карточка удалена');
    } catch (error) {
      const status = (error as { status?: number })?.status;
      notify(status === 403 || status === 401
        ? 'Ошибка: недостаточно прав для удаления'
        : getPbErrorMessage(error, 'Ошибка при удалении карточки'));
    }
  };

  const loadTablePage = async (page: number, reset = false) => {
    setTableLoading(true);
    try {
      const filters = searchQuery.trim()
        ? { ...lastFiltersRef.current, searchTerm: searchQuery.trim() }
        : Object.keys(lastFiltersRef.current).length > 0 ? lastFiltersRef.current : undefined;
      const { items, totalItems } = await fetchLeadsTable(filters, page);
      setTableLeads(prev => reset ? items : [...prev, ...items]);
      setTableTotal(totalItems);
      setTablePage(page);
    } finally {
      setTableLoading(false);
    }
  };

  const handleViewModeChange = async (mode: 'kanban' | 'table') => {
    setViewMode(mode);
    if (mode === 'table') {
      await loadTablePage(1, true);
    }
  };

  const handleTableLoadMore = () => loadTablePage(tablePage + 1);

  const handleCreateLead = async (data: Partial<Lead>) => {
    if (!currentUser) return;

    try {
      await createLead({
        ...data,
        userId: data.userId || currentUser.id,
      });
      setIsCreateModalOpen(false);
      notify('Лид создан');
    } catch {
      notify('Ошибка при создании лида');
    }
  };

  if (!currentUser) return null;

  return (
    <div id="app-screen">
      <Topbar
        user={currentUser}
        searchQuery={searchQuery}
        viewMode={viewMode}
        onCreateLead={() => setIsCreateModalOpen(true)}
        onLogout={logout}
        onSearchChange={setSearchQuery}
        onViewModeChange={handleViewModeChange}
        onSearchSelect={cardModal.openModal}
        onSidebarToggle={toggleSidebar}
        filterDropdown={(
          <FilterDropdown
            isOpen={filterStore.isOpen}
            hasActiveFilters={filterStore.hasActiveFilters}
            initialFilters={filterStore.filters}
            stages={stages}
            allTags={allTags}
            groups={currentUser?.role === 'ADMIN' ? adminGroups : undefined}
            showManagerFilter={canUseFilters}
            onToggle={filterStore.toggleOpen}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            onClose={filterStore.close}
          />
        )}
      />
      <div className="app-body">
        <MainSidebar isAdmin={currentUser?.role === 'ADMIN'} isManager={currentUser?.role === 'MANAGER'} />
        <div className="app-content">
          {viewMode === 'kanban' ? (
            <KanbanBoard
              stages={visibleStages}
              filters={boardFilters}
              isFiltered={isFiltered}
              canManage={canUseFilters}
              onCardClick={cardModal.openModal}
              onMoveLead={handleMoveLead}
              onBulkUpdate={handleBulkUpdate}
              isLoading={isBoardLoading}
            />
          ) : (
            <LeadsTable
              leads={tableLeads}
              stages={stages}
              isLoading={tableLoading}
              hasMore={tableLeads.length < tableTotal}
              onRowClick={cardModal.openModal}
              onLoadMore={handleTableLoadMore}
            />
          )}
        </div>
      </div>
      <CardModal
        lead={selectedLead}
        stages={stages}
        isAdmin={Boolean(canUseFilters)}
        history={modalHistory}
        actions={modalActions}
        smsLogs={smsLogs}
        isOpen={cardModal.isOpen}
        notFound={cardNotFound && !cardNotFoundDismissed}
        onClose={cardNotFound && !cardNotFoundDismissed ? () => setCardNotFoundDismissed(true) : cardModal.closeModal}
        onSave={handleSave}
        onDelete={handleDeleteLead}
        onAddComment={handleAddComment}
        onActivityChanged={handleReloadCardActivity}
        onNotify={notify}
      />
      <CreateLeadModal
        isOpen={isCreateModalOpen}
        stages={stages}
        isAdmin={Boolean(canUseFilters)}
        defaultManager={{ id: currentUser.id, name: currentUser.name }}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateLead}
      />
      <Notify message={msg} onDone={clear} />
    </div>
  );
};

export { KanbanPage };
